import React, { useState, useEffect, useId } from 'react';

const TimeRangeSelector = ({
    minHour,
    maxHour,
    institutionMinHour = 0,
    institutionMaxHour = 24,
    onChange,
    className = ''
}) => {
    const [validationError, setValidationError] = useState('');
    const minHourId = useId();
    const maxHourId = useId();

    // Generate hour options within institution bounds
    const generateHourOptions = () => {
        const options = [];
        for (let hour = institutionMinHour; hour <= institutionMaxHour; hour++) {
            const hourStr = hour.toString().padStart(2, '0');
            options.push({ value: hour, label: `${hourStr}:00` });
        }
        return options;
    };

    const hourOptions = generateHourOptions();

    // Validate time range whenever values change
    useEffect(() => {
        validateTimeRange(minHour, maxHour);
    }, [minHour, maxHour, institutionMinHour, institutionMaxHour]);

    const validateTimeRange = (min, max) => {
        let error = '';

        // Check if min is less than max
        if (min >= max) {
            error = 'Minimum hour must be less than maximum hour';
        }
        // Check if values are within institution bounds
        else if (min < institutionMinHour || min > institutionMaxHour) {
            error = `Minimum hour must be between ${institutionMinHour}:00 and ${institutionMaxHour}:00`;
        }
        else if (max < institutionMinHour || max > institutionMaxHour) {
            error = `Maximum hour must be between ${institutionMinHour}:00 and ${institutionMaxHour}:00`;
        }

        setValidationError(error);
        return error === '';
    };

    const handleMinHourChange = (e) => {
        const newMinHour = parseInt(e.target.value, 10);
        const isValid = validateTimeRange(newMinHour, maxHour);

        if (onChange) {
            onChange(newMinHour, maxHour, isValid);
        }
    };

    const handleMaxHourChange = (e) => {
        const newMaxHour = parseInt(e.target.value, 10);
        const isValid = validateTimeRange(minHour, newMaxHour);

        if (onChange) {
            onChange(minHour, newMaxHour, isValid);
        }
    };

    const selectClassName = `
    w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 text-sm
    ${validationError
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
        }
  `;

    return (
        <div className={`space-y-2 ${className}`}>
            <div className="flex gap-4 items-end">
                {/* Minimum Hour */}
                <div className="flex-1">
                    <label htmlFor={minHourId} className="block text-sm font-medium text-gray-700 mb-1">
                        Minimum Hour
                    </label>
                    <select
                        id={minHourId}
                        value={minHour}
                        onChange={handleMinHourChange}
                        className={selectClassName}
                        aria-describedby={validationError ? 'time-range-error' : undefined}
                    >
                        {hourOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Visual separator */}
                <div className="flex-none pb-2 text-gray-400 font-medium">
                    to
                </div>

                {/* Maximum Hour */}
                <div className="flex-1">
                    <label htmlFor={maxHourId} className="block text-sm font-medium text-gray-700 mb-1">
                        Maximum Hour
                    </label>
                    <select
                        id={maxHourId}
                        value={maxHour}
                        onChange={handleMaxHourChange}
                        className={selectClassName}
                        aria-describedby={validationError ? 'time-range-error' : undefined}
                    >
                        {hourOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Validation Error Message */}
            {validationError && (
                <div
                    id="time-range-error"
                    className="text-sm text-red-600 flex items-center gap-1"
                    role="alert"
                    aria-live="polite"
                >
                    <svg
                        className="w-4 h-4 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        aria-hidden="true"
                    >
                        <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                        />
                    </svg>
                    {validationError}
                </div>
            )}

            {/* Success indicator when valid */}
            {!validationError && minHour < maxHour && (
                <div className="text-sm text-green-600 flex items-center gap-1">
                    <svg
                        className="w-4 h-4 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        aria-hidden="true"
                    >
                        <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                        />
                    </svg>
                    Time range: {minHour.toString().padStart(2, '0')}:00 - {maxHour.toString().padStart(2, '0')}:00
                </div>
            )}
        </div>
    );
};

export default TimeRangeSelector;